import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Serves the trending-hashtag breakdown from a short-lived server cache so a
 * burst of hovers on the same hashtag hits the database once.
 */
export const getTagTrendBreakdown = createServerFn({ method: "GET" })
  .inputValidator((input: { tagId: string; days?: number }) => ({
    tagId: String(input.tagId ?? "").slice(0, 64),
    days: Math.min(Math.max(Number(input.days ?? 14) || 14, 1), 90),
  }))
  .handler(async ({ data }) => {
    const { getTagTrendBreakdownCached } = await import("./tag-trend.server");
    return getTagTrendBreakdownCached(data.tagId, data.days);
  });

/**
 * Lets administrators clear cached breakdowns after approving, merging,
 * archiving or blocking a hashtag, so the hover panel reflects the change
 * immediately instead of waiting out the cache window.
 */
export const invalidateTagTrendCacheFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { tagId?: string | null } | undefined) => ({
    tagId: input?.tagId ? String(input.tagId).slice(0, 64) : null,
  }))
  .handler(async ({ data, context }) => {
    const { data: roles, error } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    const allowed = (roles ?? []).some(
      (row: { role: string }) => row.role === "admin" || row.role === "owner",
    );
    if (!allowed) throw new Error("Forbidden: administrators only");

    const { invalidateTagTrendCache } = await import("./tag-trend.server");
    return { cleared: invalidateTagTrendCache(data.tagId ?? undefined) };
  });

/**
 * Cache hit/miss counters and render latency for the trending hover
 * breakdown, so administrators can confirm the cache is absorbing load.
 */
export const getTagTrendMetricsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { reset?: boolean } | undefined) => ({ reset: Boolean(input?.reset) }))
  .handler(async ({ data, context }) => {
    const { data: roles, error } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    const allowed = (roles ?? []).some(
      (row: { role: string }) => row.role === "admin" || row.role === "owner",
    );
    if (!allowed) throw new Error("Forbidden: administrators only");

    const mod = await import("./tag-trend.server");
    const snapshot = mod.tagTrendMetrics();
    if (data.reset) mod.resetTagTrendMetrics();
    return snapshot;
  });
